import { useState, useEffect, useCallback } from 'react';
import { api, getErrorMessage } from '../App';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { 
  Hotel, ArrowLeft, RefreshCw, DollarSign, Save, Calendar,
  Building2, Plus, Edit, ChevronRight
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount || 0);
};

export default function BudgetAllocations({ user, selectedCompany }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Data
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [allocations, setAllocations] = useState([]);
  
  // Selection
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  
  // Edit mode
  const [editMode, setEditMode] = useState(false);
  const [editedAllocations, setEditedAllocations] = useState({});
  const [projections, setProjections] = useState({
    projected_room_nights: 0,
    projected_guest_nights: 0,
    projected_revenue: 0
  });
  
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch categories and items
      const [categoriesRes, itemsRes, dashboardRes] = await Promise.all([
        api.get('/budget/categories'),
        api.get('/budget/items'),
        api.get('/budget/dashboard')  // This gives us the hotels list
      ]);
      
      setCategories(categoriesRes.data);
      setItems(itemsRes.data);
      setHotels(dashboardRes.data.hotels || []);
      
      // Auto-select first hotel if available
      if (dashboardRes.data.hotels?.length > 0 && !selectedHotel) {
        setSelectedHotel(dashboardRes.data.hotels[0].id);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load data'));
    } finally {
      setLoading(false);
    }
  }, [selectedHotel]);

  const fetchAllocations = useCallback(async () => {
    if (!selectedHotel) return;
    
    try {
      const res = await api.get(`/budget/allocations?hotel_id=${selectedHotel}&year=${selectedYear}&month=${selectedMonth}`);
      setAllocations(res.data);
      
      // Initialize edited allocations
      const initial = {};
      res.data.forEach(alloc => {
        initial[alloc.category_id] = alloc.budgeted_amount || 0;
      });
      
      // Add categories that don't have allocations yet
      categories.forEach(cat => {
        if (!initial[cat.id]) {
          initial[cat.id] = 0;
        }
      });
      
      setEditedAllocations(initial);
      
      // Get projections from first allocation or default
      if (res.data.length > 0) {
        setProjections({
          projected_room_nights: res.data[0].projected_room_nights || 0,
          projected_guest_nights: res.data[0].projected_guest_nights || 0,
          projected_revenue: res.data[0].projected_revenue || 0
        });
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load allocations'));
    }
  }, [selectedHotel, selectedYear, selectedMonth, categories]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (selectedHotel && categories.length > 0) {
      fetchAllocations();
    }
  }, [fetchAllocations, selectedHotel, categories.length]);

  const handleSaveAllocations = async () => {
    if (!selectedHotel) {
      toast.error('Please select a hotel');
      return;
    }
    
    try {
      setSaving(true);
      
      // Build allocations array
      const allocationsData = Object.entries(editedAllocations).map(([categoryId, amount]) => ({
        category_id: categoryId,
        budgeted_amount: amount || 0,
        item_allocations: {}
      }));
      
      await api.post('/budget/allocations/bulk', {
        hotel_id: selectedHotel,
        year: selectedYear,
        month: selectedMonth,
        allocations: allocationsData,
        projected_room_nights: projections.projected_room_nights,
        projected_guest_nights: projections.projected_guest_nights,
        projected_revenue: projections.projected_revenue
      });
      
      toast.success(`Budget saved for ${MONTHS[selectedMonth - 1]} ${selectedYear}`);
      setEditMode(false);
      fetchAllocations();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save budget'));
    } finally {
      setSaving(false);
    }
  };

  const getItemsByCategory = (categoryId) => items.filter(i => i.category_id === categoryId);
  
  const getAllocationForCategory = (categoryId) => {
    return allocations.find(a => a.category_id === categoryId);
  };

  const calculateTotalBudget = () => {
    return Object.values(editedAllocations).reduce((sum, val) => sum + (val || 0), 0);
  };

  const getHotelName = (hotelId) => {
    const hotel = hotels.find(h => h.id === hotelId);
    return hotel?.name || 'Unknown Hotel';
  };

  return (
    <div className="space-y-6" data-testid="budget-allocations">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/budget/setup')} className="mb-2">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Setup
          </Button>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Hotel className="h-8 w-8 text-teal-500" />
            Hotel Budget Allocations
          </h1>
          <p className="text-gray-600 mt-1">Set monthly budgets for each hotel by category</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchAllocations}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Selection Controls */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4 items-end">
            <div className="flex-1">
              <Label>Hotel</Label>
              <Select value={selectedHotel || ''} onValueChange={setSelectedHotel}>
                <SelectTrigger>
                  <SelectValue placeholder="Select Hotel" />
                </SelectTrigger>
                <SelectContent>
                  {hotels.map(hotel => (
                    <SelectItem key={hotel.id} value={hotel.id}>
                      <div className="flex items-center gap-2">
                        <Hotel className="h-4 w-4 text-gray-400" />
                        {hotel.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-32">
              <Label>Year</Label>
              <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(Number(v))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {years.map(year => (
                    <SelectItem key={year} value={String(year)}>{year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-40">
              <Label>Month</Label>
              <Select value={String(selectedMonth)} onValueChange={(v) => setSelectedMonth(Number(v))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MONTHS.map((month, idx) => (
                    <SelectItem key={idx} value={String(idx + 1)}>{month}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {!editMode && selectedHotel && (
              <Button onClick={() => setEditMode(true)} className="bg-teal-600 hover:bg-teal-700">
                <Edit className="h-4 w-4 mr-2" />
                Edit Budget
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="h-8 w-8 border-4 border-teal-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : !selectedHotel ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Hotel className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-700 mb-2">Select a Hotel</h3>
            <p className="text-gray-500">Choose a hotel from the dropdown to view or edit budget allocations</p>
          </CardContent>
        </Card>
      ) : categories.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <DollarSign className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-700 mb-2">No Budget Categories</h3>
            <p className="text-gray-500 mb-4">Set up budget categories first before allocating budgets to hotels</p>
            <Button onClick={() => navigate('/admin/budget/setup')} variant="outline">
              Go to Budget Setup
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Summary Card */}
          <Card className="bg-gradient-to-r from-teal-50 to-teal-100 border-teal-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-teal-700">
                    {getHotelName(selectedHotel)} - {MONTHS[selectedMonth - 1]} {selectedYear}
                  </p>
                  <p className="text-3xl font-bold text-teal-900">
                    {formatCurrency(calculateTotalBudget())}
                  </p>
                  <p className="text-sm text-teal-600">Total Monthly Budget</p>
                </div>
                <Calendar className="h-12 w-12 text-teal-500 opacity-50" />
              </div>
            </CardContent>
          </Card>

          {/* Edit Mode: Projections */}
          {editMode && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Projected Occupancy (for Variable Cost Calculations)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label>Projected Room Nights</Label>
                    <Input
                      type="number"
                      value={projections.projected_room_nights}
                      onChange={(e) => setProjections({ ...projections, projected_room_nights: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                  <div>
                    <Label>Projected Guest Nights</Label>
                    <Input
                      type="number"
                      value={projections.projected_guest_nights}
                      onChange={(e) => setProjections({ ...projections, projected_guest_nights: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                  <div>
                    <Label>Projected Revenue (₹)</Label>
                    <Input
                      type="number"
                      value={projections.projected_revenue}
                      onChange={(e) => setProjections({ ...projections, projected_revenue: parseFloat(e.target.value) || 0 })}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Budget Allocations Table */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Budget by Category</CardTitle>
                  <CardDescription>
                    {editMode ? 'Enter budget amounts for each category' : 'Monthly budget allocations'}
                  </CardDescription>
                </div>
                {editMode && (
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setEditMode(false)}>Cancel</Button>
                    <Button onClick={handleSaveAllocations} disabled={saving} className="bg-teal-600 hover:bg-teal-700">
                      <Save className="h-4 w-4 mr-2" />
                      {saving ? 'Saving...' : 'Save Budget'}
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Category</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Items</TableHead>
                    <TableHead className="text-right">Budget Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {categories.map((category) => {
                    const categoryItems = getItemsByCategory(category.id);
                    const allocation = getAllocationForCategory(category.id);
                    
                    return (
                      <TableRow key={category.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{category.name}</p>
                            {category.description && (
                              <p className="text-sm text-gray-500">{category.description}</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={category.category_type === 'fixed' ? 'default' : 'secondary'}>
                            {category.category_type === 'fixed' ? 'Fixed' : 'Variable'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm text-gray-500">
                            {categoryItems.map(item => item.name).join(', ') || 'No items'}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          {editMode ? (
                            <Input
                              type="number"
                              value={editedAllocations[category.id] || 0}
                              onChange={(e) => setEditedAllocations({
                                ...editedAllocations,
                                [category.id]: parseFloat(e.target.value) || 0
                              })}
                              className="w-32 text-right ml-auto"
                            />
                          ) : (
                            <span className="font-semibold">
                              {formatCurrency(allocation?.budgeted_amount || 0)}
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {/* Total Row */}
                  <TableRow className="bg-gray-50 font-semibold">
                    <TableCell colSpan={3}>Total Monthly Budget</TableCell>
                    <TableCell className="text-right text-lg">
                      {formatCurrency(calculateTotalBudget())}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Copy Budget Feature */}
          {!editMode && allocations.length > 0 && (
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Quick Actions</p>
                    <p className="text-sm text-gray-500">Copy this month's budget to other months</p>
                  </div>
                  <Button variant="outline" onClick={() => toast.info('Feature coming soon: Copy budget to other months')}>
                    Copy to Other Months
                    <ChevronRight className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
